import {
    ArrowClockwiseIcon,
    ArrowCounterClockwiseIcon,
} from '@phosphor-icons/react';
import { Pause, Play } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useVideoPlayerContext } from '@/components/VideoPlayer/VideoPlayerProvider';
import { SkipDirection } from '@/components/VideoPlayer/hooks/useSkipIndicator';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/Tooltip';
import { Button } from '@/components/ui/Button';
import { KeyHint } from '@/components';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { cn } from '@/lib/utils';
import { SKIP_SECONDS } from '@/constants/video-player';

const skipButtonClassName =
    'relative inline-flex size-6 shrink-0 items-center justify-center border-0 bg-transparent p-0 text-white-100 drop-shadow-[0px_4px_48px_rgba(7,8,10,0.5)] transition-opacity hover:bg-transparent hover:text-white-100 hover:opacity-85 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 disabled:cursor-not-allowed disabled:opacity-50 group-data-[compact]/host:hidden';
export function PipPlayerCenterControls() {
    const { playing, togglePlay, onSkip, streaming } = useVideoPlayerContext();
    const { t } = useTranslation(I18nNamespace.VIDEO);

    const showControls = !playing;

    return (
        <div
            className={cn(
                'pointer-events-none absolute inset-0 z-[1] flex items-center justify-center transition-opacity duration-200',
                showControls
                    ? 'opacity-100'
                    : 'opacity-0 group-hover/pip:opacity-100',
            )}
        >
            <div
                className="pointer-events-auto flex h-12 items-center gap-3"
                onClick={(event) => event.stopPropagation()}
            >
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            type="button"
                            variant="ghost"
                            shape="none"
                            onClick={() =>
                                onSkip(-SKIP_SECONDS, SkipDirection.Backward)
                            }
                            disabled={streaming.live}
                            className={skipButtonClassName}
                            aria-label={t('controls.skipBackward', {
                                seconds: SKIP_SECONDS,
                            })}
                        >
                            <ArrowCounterClockwiseIcon
                                className="size-6"
                                fill="currentColor"
                            />
                            <span className="absolute inset-0 flex items-center justify-center pt-[2px] font-geist-mono text-[8px] font-bold leading-none">
                                {SKIP_SECONDS}
                            </span>
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top" sideOffset={8}>
                        {t('controls.skipBackward', {
                            seconds: SKIP_SECONDS,
                        })}
                    </TooltipContent>
                </Tooltip>

                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            type="button"
                            variant="ghost"
                            shape="none"
                            onClick={togglePlay}
                            className="h-12 w-12 rounded-full bg-[rgba(17,18,20,0.7)] p-0 text-white-100 shadow-[0px_4px_48px_-4px_rgba(7,8,10,0.5)] backdrop-blur-[20px] transition-colors hover:bg-[rgba(17,18,20,0.8)] hover:text-white-100"
                            aria-label={
                                playing
                                    ? t('controls.pause')
                                    : t('controls.play')
                            }
                        >
                            {playing ? (
                                <Pause className="size-6" fill="currentColor" />
                            ) : (
                                <Play className="size-6" fill="currentColor" />
                            )}
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top" sideOffset={8}>
                        {playing ? t('controls.pause') : t('controls.play')}
                        <KeyHint theme="light" size="esc" className="w-auto">
                            Space
                        </KeyHint>
                    </TooltipContent>
                </Tooltip>

                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button
                            type="button"
                            variant="ghost"
                            shape="none"
                            onClick={() =>
                                onSkip(SKIP_SECONDS, SkipDirection.Forward)
                            }
                            disabled={streaming.live}
                            className={skipButtonClassName}
                            aria-label={t('controls.skipForward', {
                                seconds: SKIP_SECONDS,
                            })}
                        >
                            <ArrowClockwiseIcon
                                className="size-6"
                                fill="currentColor"
                            />
                            <span className="absolute inset-0 flex items-center justify-center pt-[2px] font-geist-mono text-[8px] font-bold leading-none">
                                {SKIP_SECONDS}
                            </span>
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top" sideOffset={8}>
                        {t('controls.skipForward', {
                            seconds: SKIP_SECONDS,
                        })}
                    </TooltipContent>
                </Tooltip>
            </div>
        </div>
    );
}
